import {useLocation, useNavigate, useParams} from 'react-router-dom';
import React, {useEffect, useState} from 'react';
import {clearFileState, deleteImageMediaFile, getFileMetaData} from '../../actions/media';
import {RootState, useAppDispatch} from '../../store';
import {useSelector} from 'react-redux';
import {FileData} from '../../reducers/media';
import Breadcrumbs, {BreadCrumb, generateMediaFileBreadCrumbs} from 'src/ui-components/Breadcrumbs/breadcrumbs';
import ImageView from '../../components/image-view/ImageView';
import ImageMetadata from '../../components/image-metadata/ImageMetadata';
import ImageVersion from '../../components/image-version/ImageVersion';
import {toast, ToastContainer} from 'react-toastify';
import Tracker from '../../lib/analytics/tracker';
import {ScreenNames} from '../../lib/analytics/events';
import {encodeUrl} from '../../utils/helper';
import {apiPost} from '../../utils/requests';
import {SWFT_DIGITAL_API_HOST} from '../../constants';

export default function ImageDetails() {
    const navigate = useNavigate();

    const {fileUid, orgUid} = useParams();
    const location = useLocation();
    const transformedUid = new URLSearchParams(location.search).get('transformedUid');
    const versionNumber = new URLSearchParams(location.search).get('versionNumber');
    const view = new URLSearchParams(location.search).get('view');
    const [fileData, setFileData] = useState<FileData>();
    const [breadcrumbs, setBreadcrumbs] = useState<BreadCrumb[]>();
    const [showVersionDetails, setShowVersionDetails] = useState(false);
    const [imageRenderUrl, setImageRenderUrl] = useState('');

    const dispatch = useAppDispatch();
    const [mediaProps, profileProps] = [
        useSelector((state: RootState) => state.media),
        useSelector((state: RootState) => state.userProfile),
    ];
    const toggleVersionView = () => {
        setShowVersionDetails(!showVersionDetails);
    };

    const backAction = () => {
        navigate(-1);
    };

    useEffect(() => {
        if (mediaProps.fileData.cdn_url !== '') {
            setFileData(mediaProps.fileData);
            if (transformedUid && mediaProps.fileData.saved_images[transformedUid]) {
                setImageRenderUrl(mediaProps.fileData.saved_images[transformedUid].url);
            } else {
                setImageRenderUrl(mediaProps.fileData.cdn_url);
            }
        }
    }, [mediaProps]);

    useEffect(() => {
        let items = generateMediaFileBreadCrumbs({folder: fileData?.folder, file: fileData});
        setBreadcrumbs(items);
    }, [fileData]);

    useEffect(() => {
        if (orgUid) {
            dispatch(getFileMetaData({orgUid, fileUid: fileUid!!}));
        }
        return () => {
            dispatch(clearFileState({fileUid}));
        };
    }, []);

    const updateMediaMetadata = async () => {
        if (orgUid) {
            await dispatch(getFileMetaData({orgUid, fileUid: fileUid!!}));
        }
    };

    const handleDeleteImgVersion = async (imgId: string) => {
        if (orgUid && fileUid) {
            await dispatch(deleteImageMediaFile({orgUid: orgUid, mediaFileUid: fileUid, imageUid: imgId}));
            await updateMediaMetadata();
        }
    };

    const handleTagsSave = async (tags: string[]) => {
        const orgUid = profileProps.currentOrganization.uid;
        if (fileUid && orgUid) {
            const body = {
                org_uid: orgUid,
                media_uid: fileUid,
                tags: tags.map((tag) => tag.trim()),
                action: 'update',
            };
            const res = await apiPost<any>(SWFT_DIGITAL_API_HOST + '/media/annotations/', body);
            if (res.error) {
                toast.error('Something went wrong while saving tags. Please try again later.');
            }
            await updateMediaMetadata();
        }
    };

    const handleCaptionSave = async (caption: string) => {
        const orgUid = profileProps.currentOrganization.uid;
        if (fileUid && orgUid) {
            const body = {
                org_uid: orgUid,
                media_uid: fileUid,
                captions: [caption],
                action: 'update',
            };
            const res = await apiPost<any>(SWFT_DIGITAL_API_HOST + '/media/annotations/', body);
            if (res.error) {
                toast.error('Something went wrong while saving caption. Please try again later.');
            }
            await updateMediaMetadata();
        }
    };

    useEffect(() => {
        Tracker.setCurrentScreenName(ScreenNames.IMAGE_DETAILS);
        if (view && view === 'versions') {
            setShowVersionDetails(true);
        }
    }, []);

    return (
        <>
            <div className='sticky top-0 z-50 hidden bg-white md:block'>
                {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
            </div>
            {!showVersionDetails && (
                <div className='hidden md:flex'>
                    <ImageMetadata
                        fileData={fileData}
                        showVersion={toggleVersionView}
                        savedVersionUid={transformedUid}
                        onSaveTags={handleTagsSave}
                        onSaveCaption={handleCaptionSave}
                        versionNumber={versionNumber}
                    />
                    <ImageView
                        imageUrl={encodeUrl(imageRenderUrl)}
                        fileName={fileData?.name!}
                        fileUid={fileData?.uid!}
                        transformId={transformedUid ?? undefined}
                    />
                </div>
            )}
            {!showVersionDetails && (
                <div className='flex flex-col md:hidden'>
                    <ImageView
                        imageUrl={encodeUrl(imageRenderUrl)}
                        fileName={fileData?.name!}
                        fileUid={fileData?.uid!}
                        transformId={transformedUid ?? undefined}
                    />
                    <ImageMetadata
                        fileData={fileData}
                        showVersion={toggleVersionView}
                        savedVersionUid={transformedUid}
                        onSaveTags={handleTagsSave}
                        onSaveCaption={handleCaptionSave}
                        versionNumber={versionNumber}
                    />
                </div>
            )}
            {showVersionDetails && fileData && (
                <ImageVersion
                    file={mediaProps.fileData}
                    backAction={backAction}
                    onDeleteImgVersion={handleDeleteImgVersion}
                    orgUid={orgUid!}
                />
            )}
            <ToastContainer position={'bottom-right'} autoClose={5000} />
        </>
    );
}
